import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DateRangePicker, type DateRangeValue } from '../date-range-picker/date-range-picker';
import { ZagButton } from '../button/button';
import { ConfirmDialogComponent, type ConfirmDialogData, type ConfirmDialogResult } from '../confirm-dialog/confirm-dialog.component';
import { ZagIcon } from '../icon/icon';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import {
  ValidityDialog,
  type ValidityDialogData,
  type ValidityDialogResult,
  type ValidityValue,
} from '../validity-dialog/validity-dialog.component';

@Component({
  selector: 'zag-validity-picker',
  imports: [TranslatePipe, DateRangePicker, ZagButton, ZagIcon, TooltipDirective],
  templateUrl: './validity-picker.component.html',
  styleUrl: './validity-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ValidityPicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-validity-picker',
  },
})
export class ValidityPicker implements ControlValueAccessor {
  private readonly dialog = inject(Dialog);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly clearable = input(true);
  readonly min = input<string>();
  readonly max = input<string>();
  readonly startPlaceholder = input('');
  readonly endPlaceholder = input('');
  readonly openRangeLabel = input('');
  readonly rrule = input<string | null>(null);
  readonly allowRrule = input(true);

  private readonly datePicker = viewChild(DateRangePicker);
  private readonly range = signal<DateRangeValue>({ start: null, end: null });
  private readonly hasLocalRrule = signal(false);
  private readonly localRrule = signal<string | null>(null);
  private readonly conflictDialogOpen = signal(false);
  protected readonly currentRrule = computed(() => (this.hasLocalRrule() ? this.localRrule() : this.rrule()));
  protected readonly beforeDateRangeOpen = (): boolean => {
    if (this.currentRrule() === null) return true;
    if (this.conflictDialogOpen()) return false;
    this.openRruleConflictDialog();
    return false;
  };
  private readonly formDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: ValidityValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const datePicker = this.datePicker();
      if (datePicker) datePicker.writeValue(this.range());
    });

    effect(() => {
      const datePicker = this.datePicker();
      if (!datePicker) return;
      datePicker.registerOnChange(value => {
        this.range.set(value);
        this.emitValue(value);
      });
      datePicker.registerOnTouched(() => this.onTouched());
    });
  }

  writeValue(value: ValidityValue | null): void {
    this.range.set({ start: value?.start ?? null, end: value?.end ?? null });
    this.hasLocalRrule.set(value !== null && value !== undefined);
    this.localRrule.set(value?.rrule ?? null);
  }

  registerOnChange(fn: (value: ValidityValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  protected openRecurrenceDialog(): void {
    if (this.isDisabled() || this.readonly() || !this.allowRrule()) return;

    const value = this.range();
    const dialogRef = this.dialog.open<ValidityDialogResult, ValidityDialogData>(ValidityDialog, {
      data: {
        ...value,
        rrule: this.currentRrule(),
      },
      injector: this.injector,
      width: 'min(96vw, 1040px)',
      maxWidth: '96vw',
      height: 'min(92dvh, 980px)',
      maxHeight: '92dvh',
      autoFocus: 'dialog',
      restoreFocus: true,
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => this.applyDialogResult(result));
  }

  private applyDialogResult(result: ValidityDialogResult | undefined): void {
    if (!result) return;
    this.hasLocalRrule.set(true);
    this.localRrule.set(result.rrule);
    this.range.set({ start: result.start, end: result.end });
    this.emitValue({ start: result.start, end: result.end });
  }

  private openRruleConflictDialog(): void {
    if (this.conflictDialogOpen()) return;
    this.conflictDialogOpen.set(true);
    const dialogRef = this.dialog.open<ConfirmDialogResult, ConfirmDialogData>(ConfirmDialogComponent, {
      data: {
        title: 'validity.rruleConflict.title',
        message: 'validity.rruleConflict.message',
        cancelText: 'common.cancel',
        secondaryText: 'validity.rruleConflict.openEditor',
        confirmText: 'validity.rruleConflict.removeAndEdit',
      },
      injector: this.injector,
      autoFocus: 'dialog',
      restoreFocus: false,
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      this.conflictDialogOpen.set(false);
      if (result === 'secondary') {
        this.openRecurrenceDialog();
        return;
      }
      if (isConfirmedDialogResult(result)) this.removeRruleAndOpenDateRange();
    });
  }

  private removeRruleAndOpenDateRange(): void {
    this.hasLocalRrule.set(true);
    this.localRrule.set(null);
    this.emitValue(this.range());
    this.datePicker()?.openPicker();
  }

  private emitValue(range: DateRangeValue): void {
    const value: ValidityValue = {
      ...range,
      rrule: this.currentRrule(),
    };
    this.onChange(value);
  }
}

function isConfirmedDialogResult(result: ConfirmDialogResult | undefined): boolean {
  return result === true || (typeof result === 'object' && result !== null && result.confirmed === true);
}
